import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useCreateGroup,
  useDissolveGroup,
  useRenameGroup,
  useSetGroupLabels,
  type WorkspaceGroup,
} from '@/entities/workspace-group';
import { ResourceLabelsDialog } from '@/features/resource-labels';
import { FormError } from '@/shared/forms';
import { Button, ConfirmDialog, Dialog, DialogActions, TextField } from '@/shared/ui';

export const GROUP_NAME_MAX = 100;

/** Name a new group (at the given place), or rename `group`. */
export function GroupNameDialog({
  workspaceId,
  group,
  place,
  onClose,
  onCreated,
}: {
  workspaceId: string;
  group?: WorkspaceGroup | undefined;
  place?: { section_id: string | null; parent_group_id: string | null } | undefined;
  onClose: () => void;
  onCreated?: ((group: WorkspaceGroup) => void) | undefined;
}) {
  const { t } = useTranslation();
  const create = useCreateGroup(workspaceId);
  const rename = useRenameGroup(workspaceId);
  const [name, setName] = useState(group?.name ?? t('groups.defaultName'));
  const mutation = group ? rename : create;
  return (
    <Dialog
      title={group ? t('groups.renameTitle', { name: group.name }) : t('groups.createTitle')}
      onClose={onClose}
    >
      <form
        data-testid="group-name-form"
        onSubmit={(event) => {
          event.preventDefault();
          const value = name.trim();
          if (value === '' || mutation.isPending) return;
          if (group) {
            rename.mutate(
              { id: group.id, name: value, expected_version: group.version },
              { onSuccess: onClose },
            );
          } else {
            create.mutate(
              { name: value, section_id: null, parent_group_id: null, ...place },
              {
                onSuccess: (created) => {
                  onCreated?.(created);
                  onClose();
                },
              },
            );
          }
        }}
      >
        <TextField
          label={t('groups.nameLabel')}
          value={name}
          maxLength={GROUP_NAME_MAX}
          autoFocus
          onFocus={(event) => {
            event.currentTarget.select();
          }}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <FormError error={mutation.error} />
        <DialogActions>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={mutation.isPending || !name.trim()}>
            {group ? t('common.save') : t('groups.create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/** Dissolving never deletes: this says so before anything happens. */
export function DissolveGroupDialog({
  workspaceId,
  group,
  onClose,
  onDone,
}: {
  workspaceId: string;
  group: WorkspaceGroup;
  onClose: () => void;
  onDone?: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  const dissolve = useDissolveGroup(workspaceId);
  return (
    <ConfirmDialog
      title={t('groups.dissolveTitle', { name: group.name })}
      message={t('groups.dissolveMessage')}
      confirmLabel={t('groups.dissolve')}
      pending={dissolve.isPending}
      onCancel={onClose}
      onConfirm={() => {
        dissolve.mutate(group.id, {
          onSuccess: () => {
            onDone?.();
            onClose();
          },
          onError: onClose,
        });
      }}
    />
  );
}

/** The labels of a group ITSELF: the shared resource label picker. */
export function GroupLabelsDialog({
  workspaceId,
  group,
  onClose,
}: {
  workspaceId: string;
  group: WorkspaceGroup;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const save = useSetGroupLabels(workspaceId);
  return (
    <ResourceLabelsDialog
      kind="group"
      title={t('resourceLabels.titleGroup', { name: group.name })}
      workspaceId={workspaceId}
      current={group.labels ?? []}
      pending={save.isPending}
      error={save.error}
      onSave={(labelIds) => {
        save.mutate({ id: group.id, labelIds }, { onSuccess: onClose });
      }}
      onClose={onClose}
    />
  );
}
